import Vue from 'vue';
import VueRouter from 'vue-router';

import Home from '@/views/Home.vue';
import Login from '@/views/Login.vue';
import ConfirmEmail from '@/views/ConfirmEmail.vue';
import PasswordReset from '@/views/PasswordReset.vue';
import PermissionDenied from '@/views/PermissionDenied.vue';
import NotFound from '@/views/NotFound.vue';

import Admin from '@/views/Admin.vue';
import AdminDiary from '@/components/admin/Diary.vue';
import AdminListView from '@/components/admin/AdminListView.vue';
import AdminBookingEdit from '@/components/admin/BookingEdit.vue';
import AdminBookingCheckIn from '@/components/admin/BookingCheckIn.vue';
import AdminVehicleEdit from '@/components/admin/VehicleEdit.vue';
import AdminDiscountEdit from '@/components/admin/DiscountEdit.vue';
import AdminReports from '@/components/admin/Reports.vue';
import AdminImports from '@/components/admin/Imports.vue';
import AdminContactView from '@/components/admin/ContactView.vue';
import AdminLotEdit from '@/components/admin/LotEdit.vue';
import AdminAirlineEdit from '@/components/admin/AirlineEdit.vue';
import AdminFlightEdit from '@/components/admin/FlightEdit.vue';
import AdminBusTripEdit from '@/components/admin/BusTripEdit.vue';
import AdminVehicleTypeEdit from '@/components/admin/VehicleTypeEdit.vue';
import AdminExtraEdit from '@/components/admin/ExtraEdit.vue';
import AdminRateDayEdit from '@/components/admin/RateDayEdit.vue';
import AdminCustomerEdit from '@/components/admin/CustomerEdit.vue';
import AdminDayEdit from '@/components/admin/DayEdit.vue';
import AdminBlockoutPeriodEdit from '@/components/admin/BlockoutPeriodEdit.vue';

import Profile from '@/views/Profile.vue';
import MyBookings from '@/views/MyBookings.vue';
import MyVehicles from '@/views/MyVehicles.vue';

import Booking from '@/views/Booking.vue';
import BookingStep1 from '@/components/booking/BookingStep1.vue';
import BookingStep2 from '@/components/booking/BookingStep2.vue';
import BookingStep3 from '@/components/booking/BookingStep3.vue';
import BookingStep4 from '@/components/booking/BookingStep4.vue';
import BookingStep5 from '@/components/booking/BookingStep5.vue';
import BookingStep6 from '@/components/booking/BookingStep6.vue';
import BookingStep7 from '@/components/booking/BookingStep7.vue';
import BookingStep8 from '@/components/booking/BookingStep8.vue';

import DynamicComponent from '@/views/DynamicComponent.vue';

import { authModule, routerModule } from '@/store';

Vue.use(VueRouter);

const routes = [
  {
    path: '/',
    name: 'home',
    component: Home,
    meta: {
      title: 'Home',
    },
  },
  {
    path: '/booking',
    name: 'booking',
    component: Booking,
    redirect: '/booking/step-1',
    meta: {
      title: 'Booking',
    },
    children: [
      {
        path: 'step-1',
        name: 'booking-step-1',
        component: BookingStep1,
        meta: { title: 'Get Quote' },
      },
      {
        path: 'step-2',
        name: 'booking-step-2',
        component: BookingStep2,
        meta: { title: 'Details' },
      },
      {
        path: 'step-3',
        name: 'booking-step-3',
        component: BookingStep3,
        meta: { title: 'Car Details' },
      },
      {
        path: 'step-4',
        name: 'booking-step-4',
        component: BookingStep4,
        meta: { title: 'Extras' },
      },
      {
        path: 'step-5',
        name: 'booking-step-5',
        component: BookingStep5,
        meta: { title: 'Checkout' },
      },
      {
        path: 'step-6',
        name: 'booking-step-6',
        component: BookingStep6,
        meta: { title: 'Booking Summary' },
      },
      {
        path: 'step-7',
        name: 'booking-step-7',
        component: BookingStep7,
        meta: { title: 'Payment Details' },
      },
      {
        path: 'step-8',
        name: 'booking-step-8',
        component: BookingStep8,
        meta: { title: 'Success' },
      },
    ],
  },
  {
    path: '/confirm-email',
    name: 'confirm-email',
    component: ConfirmEmail,
    meta: {
      title: 'Confirm your email',
    },
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: PasswordReset,
    meta: {
      title: 'Forgotten your password',
    },
  },
  {
    path: '/set-password',
    name: 'set-password',
    component: PasswordReset,
    meta: {
      title: 'Set your password',
    },
  },
  {
    path: '/admin',
    name: 'admin',
    component: Admin,
    redirect: '/admin/diary',
    meta: {
      roles: ['Admins', 'Sales'],
      title: 'Admin',
    },
    children: [
      {
        path: 'diary',
        name: 'admin-diary',
        component: AdminDiary,
        meta: { title: 'Diary' },
      },
      {
        path: 'bookings',
        name: 'admin-bookings',
        component: AdminListView,
        meta: { title: 'Bookings' },
      },
      {
        path: 'bookings/add',
        name: 'admin-bookings-add',
        component: AdminBookingEdit,
        meta: { title: 'Add a Booking' },
      },
      {
        path: 'bookings/edit/:bookingId',
        name: 'admin-bookings-edit',
        component: AdminBookingEdit,
        meta: { title: 'Edit a Booking' },
      },
      {
        path: 'bookings/check-in/:bookingId',
        name: 'admin-bookings-checkin',
        component: AdminBookingCheckIn,
        meta: { title: 'Booking Check In' },
      },
      {
        path: 'customers',
        name: 'admin-customers',
        component: AdminListView,
        meta: { title: 'Customers' },
      },
      {
        path: 'customers/add',
        name: 'admin-customers-add',
        component: AdminCustomerEdit,
        meta: { title: 'Add a Customer' },
      },
      {
        path: 'customers/edit/:customerId',
        name: 'admin-customers-edit',
        component: AdminCustomerEdit,
        meta: { title: 'Edit a Customer' },
      },
      {
        path: 'vehicles',
        name: 'admin-vehicles',
        component: AdminListView,
        meta: { title: 'Vehicles' },
      },
      {
        path: 'vehicles/edit/:vehicleId',
        name: 'admin-vehicles-edit',
        component: AdminVehicleEdit,
        meta: { title: 'Edit a Vehicle' },
      },
      {
        path: 'discounts',
        name: 'admin-discounts',
        component: AdminListView,
        meta: { title: 'Discounts' },
      },
      {
        path: 'discounts/add',
        name: 'admin-discounts-add',
        component: AdminDiscountEdit,
        meta: { title: 'Add a Discount' },
      },
      {
        path: 'discounts/edit/:discountId',
        name: 'admin-discounts-edit',
        component: AdminDiscountEdit,
        meta: { title: 'Edit a Discount' },
      },
      {
        path: 'reports',
        name: 'admin-reports',
        component: AdminReports,
        meta: { title: 'Reports' },
      },
      {
        path: 'reports-inout',
        name: 'admin-reports-inout',
        component: AdminReports,
        meta: { title: 'Departing/Returning Report' },
      },
      {
        path: 'reports-extras',
        name: 'admin-reports-extras',
        component: AdminReports,
        meta: { title: 'Extras Report' },
      },
      {
        path: 'imports',
        name: 'admin-imports',
        component: AdminImports,
        meta: { title: 'Imports & Reconciliation' },
      },
      {
        path: 'contact',
        name: 'admin-contact',
        component: AdminListView,
        meta: { title: 'Contact Form' },
      },
      {
        path: 'contact/view/:messageId',
        name: 'admin-contact-view',
        component: AdminContactView,
        meta: { title: 'View Message' },
      },
      {
        path: 'lots',
        name: 'admin-lots',
        component: AdminListView,
        meta: { title: 'Lots', roles: ['Admins'] },
      },
      {
        path: 'lots/add',
        name: 'admin-lots-add',
        component: AdminLotEdit,
        meta: { title: 'Add a Lot', roles: ['Admins'] },
      },
      {
        path: 'lots/edit/:lotId',
        name: 'admin-lots-edit',
        component: AdminLotEdit,
        meta: { title: 'Edit a Lot', roles: ['Admins'] },
      },
      {
        path: 'airlines',
        name: 'admin-airlines',
        component: AdminListView,
        meta: { title: 'Airlines', roles: ['Admins'] },
      },
      {
        path: 'airlines/add',
        name: 'admin-airlines-add',
        component: AdminAirlineEdit,
        meta: { title: 'Add an Airline', roles: ['Admins'] },
      },
      {
        path: 'airlines/edit/:airlineId',
        name: 'admin-airlines-edit',
        component: AdminAirlineEdit,
        meta: { title: 'Edit an Airline', roles: ['Admins'] },
      },
      {
        path: 'flights',
        name: 'admin-flights',
        component: AdminListView,
        meta: { title: 'Flights', roles: ['Admins'] },
      },
      {
        path: 'flights/add',
        name: 'admin-flights-add',
        component: AdminFlightEdit,
        meta: { title: 'Add a Flight', roles: ['Admins'] },
      },
      {
        path: 'flights/edit/:flightId',
        name: 'admin-flights-edit',
        component: AdminFlightEdit,
        meta: { title: 'Edit a Flight', roles: ['Admins'] },
      },
      {
        path: 'bus-trips',
        name: 'admin-bustrips',
        component: AdminListView,
        meta: { title: 'Bus Trips', roles: ['Admins'] },
      },
      {
        path: 'bus-trips/add',
        name: 'admin-bustrips-add',
        component: AdminBusTripEdit,
        meta: { title: 'Add a Bus Trip', roles: ['Admins'] },
      },
      {
        path: 'bus-trips/edit/:busTripId',
        name: 'admin-bustrips-edit',
        component: AdminBusTripEdit,
        meta: { title: 'Edit a Bus Trip', roles: ['Admins'] },
      },
      {
        path: 'vehicle-types',
        name: 'admin-vehicletypes',
        component: AdminListView,
        meta: { title: 'Vehicle Types', roles: ['Admins'] },
      },
      {
        path: 'vehicle-types/add',
        name: 'admin-vehicletypes-add',
        component: AdminVehicleTypeEdit,
        meta: { title: 'Add a Vehicle Type', roles: ['Admins'] },
      },
      {
        path: 'vehicle-types/edit/:vehicleTypeId',
        name: 'admin-vehicletypes-edit',
        component: AdminVehicleTypeEdit,
        meta: { title: 'Edit an Extra', roles: ['Admins'] },
      },
      {
        path: 'extras',
        name: 'admin-extras',
        component: AdminListView,
        meta: { title: 'Extras', roles: ['Admins'] },
      },
      {
        path: 'extras/add',
        name: 'admin-extras-add',
        component: AdminExtraEdit,
        meta: { title: 'Add an Extra', roles: ['Admins'] },
      },
      {
        path: 'extras/edit/:extraId',
        name: 'admin-extras-edit',
        component: AdminExtraEdit,
        meta: { title: 'Edit an Extra', roles: ['Admins'] },
      },
      {
        path: 'rates',
        name: 'admin-rates',
        component: AdminListView,
        meta: { title: 'Rates', roles: ['Admins'] },
      },
      {
        path: 'rates/add',
        name: 'admin-rates-add',
        component: AdminRateDayEdit,
        meta: { title: 'Add a Rate', roles: ['Admins'] },
      },
      {
        path: 'rates/edit/:rateId',
        name: 'admin-rates-edit',
        component: AdminRateDayEdit,
        meta: { title: 'Edit a Rate', roles: ['Admins'] },
      },
      {
        path: 'exceptional-days',
        name: 'admin-days',
        component: AdminListView,
        meta: {
          title: 'Pricing and Availability Exceptions',
          roles: ['Admins'],
        },
      },
      {
        path: 'exceptional-days/add',
        name: 'admin-days-add',
        component: AdminDayEdit,
        meta: { title: 'Add a Day', roles: ['Admins'] },
      },
      {
        path: 'exceptional-days/edit/:dayId',
        name: 'admin-days-edit',
        component: AdminDayEdit,
        meta: { title: 'Edit a day', roles: ['Admins'] },
      },
      {
        path: 'blockout-periods',
        name: 'admin-blockout-periods',
        component: AdminListView,
        meta: { title: 'Blockout Periods', roles: ['Admins'] },
      },
      {
        path: 'blockout-periods/add',
        name: 'admin-blockout-periods-add',
        component: AdminBlockoutPeriodEdit,
        meta: { title: 'Add a Blockout Period', roles: ['Admins'] },
      },
      {
        path: 'blockout-periods/edit/:blockoutPeriodId',
        name: 'admin-blockout-periods-edit',
        component: AdminBlockoutPeriodEdit,
        meta: { title: 'Edit a Blockout Period', roles: ['Admins'] },
      },
    ],
  },
  {
    path: '/profile',
    name: 'profile',
    component: Profile,
    meta: {
      title: 'Profile',
    },
  },
  {
    path: '/bookings',
    name: 'bookings',
    component: MyBookings,
    meta: {
      title: 'My Bookings',
    },
  },
  {
    path: '/vehicles',
    name: 'vehicles',
    component: MyVehicles,
    meta: {
      title: 'My Vehicles',
    },
  },
  {
    path: '/login',
    name: 'login',
    component: Login,
    meta: {
      title: 'Login',
    },
  },
  {
    path: '/permission-denied',
    name: 'denied',
    component: PermissionDenied,
    meta: {
      title: 'Permission denied',
    },
  },
  {
    path: '/:test*',
    name: 'dynamic-component',
    component: DynamicComponent,
  },
  {
    path: '/*',
    name: 'not-found',
    component: NotFound,
    meta: {
      title: 'Not found',
    },
  },
  // {
  //   path: '/about',
  //   name: 'about',
  //   // route level code-splitting
  //   // this generates a separate chunk (about.[hash].js) for this route
  //   // which is lazy-loaded when the route is visited.
  //   component: () => import(/* webpackChunkName: "about" */ '../views/About.vue'),
  // },
];

const router = new VueRouter({
  mode: 'history',
  // base: process.env.BASE_URL,
  routes,
  scrollBehavior(to, from, savedPosition) {
    return { x: 0, y: 0 };
  },
});

// could potentially move this to App.vuee

router.beforeEach(async (to, from, next) => {
  // get the SPA ready by checking if logged in etc
  if (!authModule.ready) {
    await authModule.init();
    await routerModule.init();
  }

  // check permissions on route
  let roles;
  if (to.meta.roles) {
    roles = to.meta.roles;
  } else {
    roles = to.matched.reduce(
      (combined, route) => [...combined, ...((route.meta.roles as []) || [])],
      [],
    );
  }
  if (!authModule.canAccess(roles)) {
    if (!authModule.isLoggedIn) {
      next({
        name: 'login',
        query: { next: window.location.pathname },
      });
    } else {
      next({ name: 'denied' });
    }
  } else {
    next();
  }
});

export default router;
